﻿import { useTranslation } from "react-i18next";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";
import CookiePreferencesModal from "@/components/CookiePreferencesModal";

function Breadcrumb() {
  return (
    <nav aria-label="Kruimelpad" className="bg-white border-b border-brand-border">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-3">
        <ol className="flex items-center gap-2 text-sm font-geist text-brand-text/50">
          <li>
            <a href="/" className="hover:text-brand-blue transition-colors">Home</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <span className="text-brand-text">Cookiebeleid</span>
          </li>
        </ol>
      </div>
    </nav>
  );
}

function SchemaMarkup() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Cookiebeleid — Ramen Repair Eindhoven",
    description:
      "Welke cookies Ramen Repair Eindhoven gebruikt en hoe u uw voorkeuren beheert. Conform AVG en Nederlandse Telecommunicatiewet.",
    url: "https://ramen-repair-eindhoven.nl/cookiebeleid/",
    isPartOf: {
      "@type": "WebSite",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Cookiebeleid",
        item: "https://ramen-repair-eindhoven.nl/cookiebeleid/",
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </>
  );
}

function CookieTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mt-4">
      <table className="w-full min-w-[640px] text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-brand-yellow">
            {headers.map((h, i) => (
              <th
                key={i}
                className="text-left font-geist font-semibold text-brand-text py-3 pr-4 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-brand-border hover:bg-brand-yellow/5 transition-colors"
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="font-geist text-brand-text/80 py-3 pr-4 whitespace-nowrap"
                >
                  {ci === 2 ? (
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        cell === "Functioneel"
                          ? "bg-emerald-100 text-emerald-700"
                          : cell === "Analytisch"
                          ? "bg-amber-100 text-amber-700"
                          : cell === "Marketing"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {cell}
                    </span>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Cookiebeleid() {
  const { t } = useTranslation("cookiebeleid");
  const { openModal } = useCookieConsent();

  const sections = t("sections", { returnObjects: true }) as Array<{
    nr: string;
    title: string;
    paragraphs: string[];
    list?: string[];
    table?: { headers: string[]; rows: string[][] };
    button?: { text: string; action: string };
    links?: Array<{ text: string; url: string }>;
  }>;

  return (
    <main className="bg-white min-h-screen">
      <title>{t("seo.title")}</title>
      <meta name="description" content={t("seo.description")} />
      <meta property="og:title" content={t("seo.title")} />
      <meta property="og:description" content={t("seo.description")} />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://ramen-repair-eindhoven.nl/cookiebeleid/" />
      <meta property="og:image" content="/og/og-home.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Ramen Repair Eindhoven - kozijn en raamreparatie" />
      <link rel="canonical" href="https://ramen-repair-eindhoven.nl/cookiebeleid/" />
      <link rel="alternate" hreflang="nl-NL" href="https://ramen-repair-eindhoven.nl/cookiebeleid/" />
      <link rel="alternate" hreflang="x-default" href="https://ramen-repair-eindhoven.nl/cookiebeleid/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("seo.title")} />
      <meta name="twitter:description" content={t("seo.description")} />
      <meta name="twitter:image" content="/og/og-home.jpg" />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />

      <section className="max-w-[720px] mx-auto px-4 md:px-6 py-16 md:py-24">
        <h1 className="font-geist font-bold text-3xl md:text-4xl lg:text-5xl text-brand-text tracking-tight mb-4">
          {t("seo.h1")}
        </h1>
        <p className="font-geist text-sm text-brand-text/50 mb-16">
          {t("version")}
        </p>

        <div className="flex flex-col gap-14 md:gap-16">
          {sections.map((sec) => (
            <article key={sec.nr} className="scroll-mt-24">
              <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text tracking-tight mb-4 pb-3 border-b border-brand-border">
                <span className="text-brand-yellow mr-2">{sec.nr}</span>
                {sec.title}
              </h2>
              <div className="flex flex-col gap-3">
                {sec.paragraphs.map((p, i) => (
                  <p key={i} className="font-geist text-base text-brand-text/80 leading-relaxed">
                    {p}
                  </p>
                ))}
                {sec.list && (
                  <ul className="flex flex-col gap-2 mt-1">
                    {sec.list.map((item, i) => (
                      <li
                        key={i}
                        className="font-geist text-base text-brand-text/80 leading-relaxed flex items-start gap-2"
                      >
                        <span className="text-brand-yellow mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-yellow inline-block"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {sec.table && (
                  <CookieTable
                    headers={sec.table.headers}
                    rows={sec.table.rows}
                  />
                )}
                {sec.button && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={openModal}
                      className="inline-flex items-center gap-2 bg-brand-yellow text-brand-text font-geist font-semibold px-5 py-3 rounded-md hover:bg-brand-yellow/90 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-settings-3-line w-5 h-5 flex items-center justify-center text-base"></i>
                      {sec.button.text}
                    </button>
                  </div>
                )}
                {sec.links && (
                  <div className="flex flex-col gap-2 mt-1">
                    {sec.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-geist text-base text-brand-yellow underline underline-offset-4 hover:text-brand-text transition-colors inline-flex items-center gap-1"
                      >
                        {link.text}
                        <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-sm"></i>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        <p className="font-geist text-sm text-brand-text/50 mt-20 pt-6 border-t border-brand-border italic">
          {t("footer.version")}
        </p>
      </section>

      <Footer />
      <StickyMobile />
      <CookiePreferencesModal />
    </main>
  );
}